import { createClient } from '@supabase/supabase-js';

// Environment variables for Supabase (Web/Vite build)
// All credentials must come from environment variables (.env file)
export const supabaseUrl =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL) ||
  '';

export const supabaseAnonKey =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_ANON_KEY) ||
  '';

/**
 * Recorded rather than thrown: a module-scope throw runs before React mounts, so
 * ErrorBoundary cannot catch it and the app dies with a blank screen and no
 * diagnostic. See lib/supabase.native.ts for the same guard on the native client.
 */
export const supabaseConfigError: string | null =
  !supabaseUrl || !supabaseAnonKey
    ? `Missing Supabase configuration: ${[
        !supabaseUrl && 'VITE_SUPABASE_URL',
        !supabaseAnonKey && 'VITE_SUPABASE_ANON_KEY',
      ]
        .filter(Boolean)
        .join(' and ')}. Set these in the hosting provider project settings.`
    : null;

export const isSupabaseConfigured = supabaseConfigError === null;

if (supabaseConfigError) {
  console.error('[Supabase]', supabaseConfigError);
}

const clientUrl = supabaseUrl || 'https://unconfigured.invalid';
const clientAnonKey = supabaseAnonKey || 'unconfigured';
const isBrowser = typeof window !== 'undefined';

/**
 * Web storage adapter for Supabase Auth
 * Uses localStorage directly (browser handles security)
 */
class WebStorage {
  async getItem(key: string): Promise<string | null> {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('[WebStorage] Failed to get item:', error);
      return null;
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error('[WebStorage] Failed to set item:', error);
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('[WebStorage] Failed to remove item:', error);
    }
  }
}

// Initialize Supabase client with web storage
export const supabase = createClient(clientUrl, clientAnonKey, {
  auth: {
    storage: new WebStorage(),
    autoRefreshToken: isBrowser && isSupabaseConfigured,
    persistSession: isBrowser,
    detectSessionInUrl: isBrowser,
  },
  global: {
    headers: {
      'X-Client-Info': 'real-estate-mg-web',
    },
  },
});

// Session management with error handling
export const getSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();

    if (error) {
      console.warn('[Auth] Session retrieval error:', error.message);
      if (error.message.includes('refresh') || error.message.includes('token')) {
        await clearSession();
        return { session: null, error };
      }
    }

    return { session: data.session, error };
  } catch (error: any) {
    console.error('[Auth] Unexpected session error:', error);
    await clearSession();
    return { session: null, error };
  }
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();

    // Clear user context cache on logout
    try {
      const { clearUserContextCache } = await import('./security');
      clearUserContextCache();
    } catch (error) {
      // Ignore if security module can't be loaded
    }

    if (error) {
      console.warn('[Auth] Sign out error:', error.message);
      await clearSession();
    }

    return { error };
  } catch (error: any) {
    console.error('[Auth] Unexpected sign out error:', error);
    await clearSession();
    return { error };
  }
};

// Clear local session data
export const clearSession = async () => {
  try {
    const storage = new WebStorage();

    // Clear Supabase auth tokens
    await storage.removeItem('supabase.auth.token');
    const storageKey = `sb-${supabaseUrl.replace('https://', '').replace('.supabase.co', '')}-auth-token`;
    await storage.removeItem(storageKey);

    // Clear localStorage keys that might contain auth data
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('supabase') || key.includes('auth') || key.includes('session'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error('[Auth] Error clearing session:', error);
  }
};

// Check if user is authenticated with error handling
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    const { session, error } = await getSession();

    if (error || !session) {
      return false;
    }

    // Check if session is expired
    if (session.expires_at && new Date(session.expires_at * 1000) < new Date()) {
      await clearSession();
      return false;
    }

    return true;
  } catch (error) {
    console.error('[Auth] Authentication check failed:', error);
    return false;
  }
};

// Refresh session with error handling
export const refreshSession = async () => {
  try {
    const { data, error } = await supabase.auth.refreshSession();

    if (error) {
      console.warn('[Auth] Session refresh failed:', error.message);
      await clearSession();
      return { session: null, error };
    }
    return { session: data.session, error: null };
  } catch (error: any) {
    console.error('[Auth] Unexpected refresh error:', error);
    await clearSession();
    return { session: null, error };
  }
};

// Handle auth state changes
if (supabase && supabase.auth) {
  supabase.auth.onAuthStateChange(async (event, session) => {
    console.log('[Auth] State change:', event);
    if (event === 'SIGNED_OUT') {
      await clearSession();
    }
  });
}
